import {
  Banknote,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Menu,
  Package,
  Search,
  ShoppingCart,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { BandeauHorsLigne } from '@/components/ui/bandeau-hors-ligne';
import { Logo } from '@/components/ui/logo';
import { MenuCompte } from '@/features/auth/components/MenuCompte';
import {
  DeclencheurRecherche,
  RechercheGlobale,
  useRaccourciRecherche,
} from '@/features/recherche/RechercheGlobale';
import { cn } from '@/lib/utils';

const NAVIGATION = [
  { to: '/', libelle: 'Tableau de bord', icone: LayoutDashboard, exact: true },
  { to: '/commandes', libelle: 'Commandes', icone: ClipboardList },
  { to: '/ventes', libelle: 'Ventes', icone: ShoppingCart },
  { to: '/caisse', libelle: 'Caisse', icone: Banknote },
  { to: '/clients', libelle: 'Clients', icone: Users },
  { to: '/stock', libelle: 'Stock', icone: Package },
  { to: '/factures', libelle: 'Factures', icone: FileText },
  { to: '/import', libelle: 'Import', icone: Upload },
] as const;

function Liens({ onNaviguer }: { onNaviguer?: () => void }) {
  return (
    <nav className="flex flex-col gap-0.5 px-3 py-4">
      {NAVIGATION.map(({ to, libelle, icone: Icone, ...reste }) => (
        <NavLink
          key={to}
          to={to}
          end={'exact' in reste ? reste.exact : false}
          onClick={onNaviguer}
          className={({ isActive }) =>
            cn(
              'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white',
            )
          }
        >
          {({ isActive }) => (
            <>
              {/* Repère moutarde de l'élément actif. */}
              <span
                aria-hidden
                className={cn(
                  'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r bg-mustard-500 transition-opacity',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}
              />
              <Icone
                aria-hidden
                className={cn('h-4 w-4 shrink-0', isActive ? 'text-mustard-500' : 'text-white/60')}
              />
              {libelle}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Coquille de l'application : barre latérale noire, contenu blanc, accent
 * moutarde sur l'élément actif. Responsive jusqu'à la tablette — en dessous de
 * `lg`, la barre latérale devient un tiroir.
 */
export function AppShell() {
  const [tiroir, setTiroir] = useState(false);
  const [palette, setPalette] = useState(false);

  useRaccourciRecherche(() => setPalette(true));

  return (
    <div className="flex min-h-full">
      {/* Barre latérale fixe à partir de lg. */}
      <aside className="sans-impression fixed inset-y-0 left-0 z-40 hidden w-60 flex-col bg-ink lg:flex">
        <div className="border-b border-white/10 px-5 py-4">
          <Logo />
        </div>
        <div className="px-3 pt-3">
          <DeclencheurRecherche onOuvrir={() => setPalette(true)} />
        </div>
        <Liens />
        <div className="mt-auto">
          <MenuCompte />
          <div className="px-5 pb-4 text-xs text-white/40">Salé, Maroc</div>
        </div>
      </aside>

      {/* Tiroir mobile / tablette. */}
      {tiroir ? (
        <div className="sans-impression fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 animate-fade-in bg-ink/50"
            onClick={() => setTiroir(false)}
            aria-hidden
          />
          <aside className="relative flex h-full w-64 animate-fade-in flex-col bg-ink">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <Logo />
              <button
                type="button"
                onClick={() => setTiroir(false)}
                className="rounded p-1 text-white/70 hover:text-white"
                aria-label="Fermer le menu"
              >
                <X aria-hidden className="h-5 w-5" />
              </button>
            </div>
            <Liens onNaviguer={() => setTiroir(false)} />
            <div className="mt-auto">
              <MenuCompte />
            </div>
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-60 print:pl-0">
        <header className="sans-impression sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-ink px-4 py-3 lg:hidden">
          <button
            type="button"
            onClick={() => setTiroir(true)}
            className="rounded p-1 text-white/80 hover:text-white"
            aria-label="Ouvrir le menu"
          >
            <Menu aria-hidden className="h-5 w-5" />
          </button>
          <Logo />
          <button
            type="button"
            onClick={() => setPalette(true)}
            className="ml-auto rounded p-1 text-white/80 hover:text-white"
            aria-label="Rechercher"
          >
            <Search aria-hidden className="h-5 w-5" />
          </button>
        </header>

        <BandeauHorsLigne />

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <RechercheGlobale ouvert={palette} onFermer={() => setPalette(false)} />
    </div>
  );
}

/** En-tête de page : titre, sous-titre et actions. */
export function PageHeader({
  titre,
  description,
  actions,
}: {
  titre: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line bg-white px-4 py-4 sm:px-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-ink">{titre}</h1>
        {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Corps de page, avec la marge standard. */
export function PageBody({ children }: { children: ReactNode }) {
  return <div className="p-4 sm:p-6">{children}</div>;
}
